import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatIDR, formatDateTime, formatDate } from "../lib/format";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { CheckCircle2, TrendingUp, TrendingDown, Wallet, Download, Clock } from "lucide-react";
import * as XLSX from "xlsx";

const exportXLSX = (rows, sheet, filename) => {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheet);
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

const BUCKET_LABEL = {
  not_due: "Belum Jatuh Tempo",
  "0_30": "0 - 30 hari",
  "31_60": "31 - 60 hari",
  "60_plus": "> 60 hari",
};
const BUCKET_COLOR = {
  not_due: "bg-slate-100 text-slate-700 border-slate-200",
  "0_30": "bg-yellow-100 text-yellow-800 border-yellow-200",
  "31_60": "bg-orange-100 text-orange-800 border-orange-200",
  "60_plus": "bg-red-100 text-red-800 border-red-200",
};

const AP_BUCKET = [
  { key: "overdue", label: "OVERDUE", color: "text-red-700 bg-red-50 border-red-200" },
  { key: "due_today", label: "Jatuh Tempo Hari Ini", color: "text-orange-700 bg-orange-50 border-orange-200" },
  { key: "due_3", label: "3 Hari Lagi", color: "text-amber-700 bg-amber-50 border-amber-200" },
  { key: "due_7", label: "7 Hari Lagi", color: "text-yellow-700 bg-yellow-50 border-yellow-200" },
  { key: "due_14", label: "14 Hari Lagi", color: "text-sky-700 bg-sky-50 border-sky-200" },
  { key: "not_due", label: "> 14 Hari", color: "text-slate-700 bg-slate-50 border-slate-200" },
];

export default function Finance() {
  const [ar, setAr] = useState([]);
  const [ap, setAp] = useState([]);
  const [cf, setCf] = useState([]);
  const [aging, setAging] = useState(null);
  const [apAging, setApAging] = useState(null);

  const load = () => Promise.all([
    api.get("/finance/ar").then((r) => setAr(r.data)),
    api.get("/finance/ap").then((r) => setAp(r.data)),
    api.get("/finance/cashflow").then((r) => setCf(r.data)),
    api.get("/finance/aging").then((r) => setAging(r.data)),
    api.get("/finance/ap-aging").then((r) => setApAging(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  const payAp = async (id) => {
    if (!window.confirm("Tandai utang sebagai LUNAS?")) return;
    try { await api.post(`/accounts-payable/${id}/pay`); toast.success("Utang dilunasi — jurnal tercatat"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const totalIn = cf.filter((c) => c.type === "in").reduce((s, c) => s + c.amount, 0);
  const totalOut = cf.filter((c) => c.type === "out").reduce((s, c) => s + c.amount, 0);

  const exportAR = () => exportXLSX(ar.map((i) => ({
    "No. Invoice": i.invoice_number, "Customer": i.customer_name,
    "Tanggal Terbit": formatDate(i.issue_date), "Jatuh Tempo": i.due_date ? formatDate(i.due_date) : "-",
    "Subtotal": i.subtotal, "PPN 12%": i.ppn, "Total": i.total, "Status": i.status,
  })), "Piutang", `AR_${new Date().toISOString().slice(0,10)}`);

  const exportAP = () => exportXLSX(ap.map((i) => ({
    "No. PO": i.po_number, "Supplier": i.supplier_name,
    "Tanggal": formatDate(i.created_at), "Jumlah": i.amount, "Status": i.status,
  })), "Utang", `AP_${new Date().toISOString().slice(0,10)}`);

  const exportCF = () => exportXLSX(cf.map((c) => ({
    "Tanggal": formatDateTime(c.date), "Jenis": c.type === "in" ? "MASUK" : "KELUAR",
    "Kategori": c.category, "Referensi": c.reference, "Jumlah": c.amount,
  })), "ArusKas", `CashFlow_${new Date().toISOString().slice(0,10)}`);

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div>
        <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Modul Keuangan</div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">Finance Operasional</h1>
        <p className="text-sm text-slate-500 mt-1">Piutang, Utang, Arus Kas, dan Aging Report — semua tersinkron dengan Jurnal Akuntansi.</p>
      </div>

      <Tabs defaultValue="ar">
        <TabsList data-testid="finance-tabs">
          <TabsTrigger value="ar" data-testid="tab-ar">Piutang (AR)</TabsTrigger>
          <TabsTrigger value="ap" data-testid="tab-ap">Utang (AP)</TabsTrigger>
          <TabsTrigger value="ap-aging" data-testid="tab-ap-aging">Aging Utang</TabsTrigger>
          <TabsTrigger value="cf" data-testid="tab-cf">Arus Kas</TabsTrigger>
          <TabsTrigger value="aging" data-testid="tab-aging">Aging Piutang</TabsTrigger>
        </TabsList>

        <TabsContent value="ar">
          <Card className="card-elevated"><CardContent className="p-4">
            <div className="flex justify-end mb-3"><Button variant="outline" size="sm" onClick={exportAR} data-testid="export-ar-btn"><Download size={14} className="mr-1" />Excel</Button></div>
            <Table data-testid="ar-table">
              <TableHeader><TableRow><TableHead>No. Invoice</TableHead><TableHead>Customer</TableHead><TableHead>Terbit</TableHead><TableHead>Jatuh Tempo</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {ar.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell className="font-mono text-xs">{i.invoice_number}</TableCell>
                    <TableCell>{i.customer_name}</TableCell>
                    <TableCell className="text-sm">{formatDate(i.issue_date)}</TableCell>
                    <TableCell className="text-sm">{i.due_date ? formatDate(i.due_date) : "-"}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{formatIDR(i.total)}</TableCell>
                    <TableCell><Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100">UNPAID</Badge></TableCell>
                  </TableRow>
                ))}
                {ar.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Tidak ada piutang</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="ap">
          <Card className="card-elevated"><CardContent className="p-4">
            <div className="flex justify-end mb-3"><Button variant="outline" size="sm" onClick={exportAP} data-testid="export-ap-btn"><Download size={14} className="mr-1" />Excel</Button></div>
            <Table data-testid="ap-table">
              <TableHeader><TableRow><TableHead>No. PO</TableHead><TableHead>Supplier</TableHead><TableHead>Tanggal</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {ap.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell className="font-mono text-xs">{i.po_number}</TableCell>
                    <TableCell>{i.supplier_name}</TableCell>
                    <TableCell>{formatDateTime(i.created_at)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{formatIDR(i.amount)}</TableCell>
                    <TableCell>
                      {i.status === "paid"
                        ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">LUNAS</Badge>
                        : <Badge className="bg-red-100 text-red-700 border-red-200 hover:bg-red-100">UNPAID</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      {i.status === "unpaid" && (
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => payAp(i.id)} data-testid={`ap-pay-${i.po_number}`}>
                          <CheckCircle2 size={14} className="mr-1" />Bayar
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {ap.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Tidak ada utang</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="ap-aging">
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
            {AP_BUCKET.map((b) => (
              <Card key={b.key} className="card-elevated"><CardContent className={`p-3 border-l-4 ${b.color}`}>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><Clock size={11} />{b.label}</div>
                <div className="font-display text-lg font-extrabold mt-1">{formatIDR(apAging?.totals?.[b.key] || 0)}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{apAging?.buckets?.[b.key]?.length || 0} utang</div>
              </CardContent></Card>
            ))}
          </div>
          <Card className="card-elevated"><CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-slate-800">Rincian Aging Utang (Prioritas Bayar)</div>
              <Button variant="outline" size="sm" onClick={() => {
                const rows = [];
                AP_BUCKET.forEach((b) => {
                  (apAging?.buckets?.[b.key] || []).forEach((ap) => rows.push({
                    "Bucket": b.label, "No. PO": ap.po_number, "Supplier": ap.supplier_name,
                    "Termin": ap.payment_term, "Jatuh Tempo": ap.due_date, "Hari Lagi": ap.days_left,
                    "Total": ap.amount, "Deskripsi": ap.description || "",
                  }));
                });
                exportXLSX(rows, "AP_Aging", `AP_Aging_${new Date().toISOString().slice(0,10)}`);
              }} data-testid="export-ap-aging-btn"><Download size={14} className="mr-1" />Excel</Button>
            </div>
            <Table data-testid="ap-aging-table">
              <TableHeader><TableRow>
                <TableHead>Bucket</TableHead><TableHead>No. PO</TableHead><TableHead>Supplier</TableHead>
                <TableHead>Termin</TableHead><TableHead>Jatuh Tempo</TableHead>
                <TableHead className="text-right">Hari Lagi</TableHead>
                <TableHead className="text-right">Total</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {apAging && AP_BUCKET.flatMap((b) => (apAging.buckets[b.key] || []).map((ap) => (
                  <TableRow key={ap.id} className={b.key === "overdue" ? "bg-red-50/40" : b.key === "due_today" ? "bg-orange-50/40" : ""}>
                    <TableCell><Badge className={b.color.replace("text-","bg-").replace("bg-","bg-") + " border"}>{b.label}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{ap.po_number}</TableCell>
                    <TableCell>{ap.supplier_name}</TableCell>
                    <TableCell className="text-xs">{ap.payment_term}{ap.description ? ` — ${ap.description}` : ""}</TableCell>
                    <TableCell className="text-sm">{ap.due_date ? formatDate(ap.due_date) : "-"}</TableCell>
                    <TableCell className="text-right font-mono">{ap.days_left < 0 ? `+${ap.days_overdue}` : ap.days_left}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{formatIDR(ap.amount)}</TableCell>
                  </TableRow>
                )))}
                {apAging && Object.values(apAging.buckets).every((v) => v.length === 0) && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Tidak ada utang unpaid</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="cf">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <Card className="card-elevated"><CardContent className="p-4 flex items-center gap-3"><TrendingUp className="text-emerald-500" /><div><div className="text-[11px] uppercase tracking-widest text-slate-500">Kas Masuk</div><div className="font-display text-lg font-extrabold text-emerald-700">{formatIDR(totalIn)}</div></div></CardContent></Card>
            <Card className="card-elevated"><CardContent className="p-4 flex items-center gap-3"><TrendingDown className="text-red-500" /><div><div className="text-[11px] uppercase tracking-widest text-slate-500">Kas Keluar</div><div className="font-display text-lg font-extrabold text-red-700">{formatIDR(totalOut)}</div></div></CardContent></Card>
            <Card className="card-elevated"><CardContent className="p-4 flex items-center gap-3"><Wallet className="text-navy-900" /><div><div className="text-[11px] uppercase tracking-widest text-slate-500">Saldo Bersih</div><div className="font-display text-lg font-extrabold text-navy-900">{formatIDR(totalIn - totalOut)}</div></div></CardContent></Card>
          </div>
          <Card className="card-elevated"><CardContent className="p-4">
            <div className="flex justify-end mb-3"><Button variant="outline" size="sm" onClick={exportCF} data-testid="export-cf-btn"><Download size={14} className="mr-1" />Excel</Button></div>
            <Table data-testid="cf-table">
              <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Jenis</TableHead><TableHead>Kategori</TableHead><TableHead>Referensi</TableHead><TableHead className="text-right">Jumlah</TableHead></TableRow></TableHeader>
              <TableBody>
                {cf.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{formatDateTime(c.date)}</TableCell>
                    <TableCell>{c.type === "in" ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">MASUK</Badge> : <Badge className="bg-red-100 text-red-700 border-red-200 hover:bg-red-100">KELUAR</Badge>}</TableCell>
                    <TableCell>{c.category}</TableCell>
                    <TableCell className="font-mono text-xs">{c.reference}</TableCell>
                    <TableCell className={`text-right font-mono font-semibold ${c.type === "in" ? "text-emerald-700" : "text-red-700"}`}>{c.type === "in" ? "+" : "-"} {formatIDR(c.amount)}</TableCell>
                  </TableRow>
                ))}
                {cf.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400">Belum ada transaksi</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="aging">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            {["not_due", "0_30", "31_60", "60_plus"].map((k) => (
              <Card key={k} className="card-elevated"><CardContent className="p-4">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><Clock size={12} />{BUCKET_LABEL[k]}</div>
                <div className={`font-display text-xl font-extrabold mt-1 ${k === "60_plus" ? "text-red-700" : k === "31_60" ? "text-orange-700" : k === "0_30" ? "text-yellow-700" : "text-slate-700"}`}>{formatIDR(aging?.totals?.[k] || 0)}</div>
                <div className="text-xs text-slate-500 mt-1">{aging?.buckets?.[k]?.length || 0} invoice</div>
              </CardContent></Card>
            ))}
          </div>
          <Card className="card-elevated"><CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-slate-800">Rincian Aging Piutang</div>
              <Button variant="outline" size="sm" onClick={() => {
                const rows = [];
                ["not_due", "0_30", "31_60", "60_plus"].forEach((k) => {
                  (aging?.buckets?.[k] || []).forEach((inv) => rows.push({
                    "Bucket": BUCKET_LABEL[k], "No. Invoice": inv.invoice_number,
                    "Customer": inv.customer_name, "Jatuh Tempo": inv.due_date ? formatDate(inv.due_date) : "-",
                    "Hari Terlambat": inv.days_overdue, "Total": inv.total,
                  }));
                });
                exportXLSX(rows, "Aging", `Aging_${new Date().toISOString().slice(0,10)}`);
              }} data-testid="export-aging-btn"><Download size={14} className="mr-1" />Excel</Button>
            </div>
            <Table data-testid="aging-table">
              <TableHeader><TableRow><TableHead>Bucket</TableHead><TableHead>No. Invoice</TableHead><TableHead>Customer</TableHead><TableHead>Jatuh Tempo</TableHead><TableHead className="text-right">Hari Lewat</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
              <TableBody>
                {aging && ["60_plus", "31_60", "0_30", "not_due"].flatMap((k) => (aging.buckets[k] || []).map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell><Badge className={BUCKET_COLOR[k]}>{BUCKET_LABEL[k]}</Badge></TableCell>
                    <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                    <TableCell>{inv.customer_name}</TableCell>
                    <TableCell className="text-sm">{inv.due_date ? formatDate(inv.due_date) : "-"}</TableCell>
                    <TableCell className="text-right font-mono">{inv.days_overdue}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{formatIDR(inv.total)}</TableCell>
                  </TableRow>
                )))}
                {aging && Object.values(aging.buckets).every((b) => b.length === 0) && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Tidak ada piutang</TableCell></TableRow>}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
